/**
 * Auth session state (SPEC.md §8.1, §3.2: Pinia holds client/session state only).
 *
 * The store owns *who is signed in*; the access token itself lives in the HTTP module's memory, so
 * that nothing can accidentally serialise it into devtools state or `sessionStorage`.
 */
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import type { User, UserRole } from '@/entities/user/model'
import * as authApi from '@/features/auth/api'
import { ApiError, setAccessToken } from '@/shared/api/http'
import { can as mayDo, grantedPermissions, grantedRole } from '@/shared/lib/permissions'
import { refreshSession } from '@/shared/api/http'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  // The one set, held in `shared/lib/permissions` so a shared component or a second feature
  // can ask without importing this store (§10.1). Exposed here, not copied here.
  const permissions = grantedPermissions
  /** False until the boot-time refresh attempt has finished, so guards do not act on a blank state. */
  const isReady = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const role = computed<UserRole | null>(() => user.value?.role ?? null)
  const mustChangePassword = computed(() => user.value?.must_change_password ?? false)

  // Mirrored into `shared/lib/permissions` for the cancel rule (SPEC.md §7.3). Synchronous, so a
  // component rendered straight after sign-in already sees the role it was rendered for.
  watch(role, (next) => (grantedRole.value = next), { immediate: true, flush: 'sync' })

  /**
   * True when the signed-in user's role grants `permission` (SPEC.md §8.1).
   *
   * Delegates to `shared/lib/permissions`, which is where the set actually lives so that a shared
   * component or a second feature can ask without importing this store — the reach §10.1 forbids.
   * Kept here because seventeen call sites already ask the store, and they are not wrong to.
   */
  function can(permission: string): boolean {
    return mayDo(permission)
  }

  function clear(): void {
    user.value = null
    permissions.value = new Set()
    setAccessToken(null)
  }

  async function loadMe(): Promise<void> {
    const me = await authApi.fetchMe()
    user.value = me.user
    permissions.value = new Set(me.permissions)
  }

  /**
   * The server says the password must be changed first (`PASSWORD_CHANGE_REQUIRED`).
   *
   * `/auth/me` is one of the few routes it still answers, so the profile is re-read from there —
   * the flag may have been set after sign-in, by a director resetting the password. If even that
   * fails, the flag is set locally: the router guard must hold the user on the change-password
   * screen either way, or every other screen fails one request at a time.
   */
  async function requirePasswordChange(): Promise<void> {
    try {
      await loadMe()
    } catch {
      // Handled below: the local flag is enough to keep the guard closed.
    }
    if (user.value && !user.value.must_change_password) {
      user.value = { ...user.value, must_change_password: true }
    }
  }

  /**
   * Restore a session on application start.
   *
   * A 401 here is the ordinary signed-out path — a browser with no refresh cookie — and must not
   * surface as an error to the user.
   */
  async function bootstrap(): Promise<void> {
    try {
      if (await refreshSession()) {
        await loadMe()
      }
    } catch {
      clear()
    } finally {
      isReady.value = true
    }
  }

  async function signIn(payload: authApi.LoginPayload): Promise<void> {
    const tokens = await authApi.login(payload)
    setAccessToken(tokens.access_token)
    user.value = tokens.user
    // Permissions are not in the login response: one place computes them (`/auth/me`), so the two
    // can never disagree.
    await loadMe()
  }

  async function signOut(): Promise<void> {
    try {
      await authApi.logout()
    } catch (error) {
      // An expired session cannot be logged out of, and the user does not care — clear locally.
      if (!(error instanceof ApiError)) {
        throw error
      }
    } finally {
      clear()
    }
  }

  async function signOutEverywhere(): Promise<number> {
    const { sessions_revoked: revoked } = await authApi.logoutAll()
    clear()
    return revoked
  }

  return {
    user,
    permissions,
    isReady,
    isAuthenticated,
    role,
    mustChangePassword,
    can,
    clear,
    loadMe,
    requirePasswordChange,
    bootstrap,
    signIn,
    signOut,
    signOutEverywhere,
  }
})
